import { compareVersions, normalizeInstalledVersion } from './coinsing-version.util';

export interface CoinSingPillResult {
  pillText: string | null;
  pillClass: 'status-sent' | 'status-paid' | 'status-not-paid' | 'status-partial' | '';
  ariaLabel: string;
  isRollback: boolean;
  reason?: string;
}

export interface CoinSingDeviceUpdateInfo {
  appVersion?: string;
  currentAppVersion?: string;
  version?: string;
  coinsingTargetVersion?: string;
  coinsingUpdateNeeded?: boolean;
  coinsingUpdate?: {
    status?: string;
    failureCode?: string;
    targetVersion?: string;
    previousVersion?: string;
    reason?: string;
    errorMessage?: string;
    [key: string]: any;
  };
  [key: string]: any;
}

/**
 * Computes update state pill for CoinSing devices in accordance with KAN-271 AC-3.
 * Uses design system `.status-*` classes, text + colour, role="status", aria-label.
 */
export function computeCoinSingUpdatePill(device: CoinSingDeviceUpdateInfo): CoinSingPillResult {
  const installedRaw = device.appVersion || device.currentAppVersion || device.version || '';
  const installedNorm = normalizeInstalledVersion(installedRaw);
  const targetVer = device.coinsingTargetVersion || device.coinsingUpdate?.targetVersion || '';
  const update = device.coinsingUpdate;
  const updateTargetVer = update?.targetVersion || '';
  // Ignore stale coinsingUpdate records from a previous target version when a different targetVersion is set on the device
  const isStaleTarget = Boolean(
    device.coinsingTargetVersion &&
    updateTargetVer &&
    (normalizeInstalledVersion(updateTargetVer) || updateTargetVer) !== (normalizeInstalledVersion(device.coinsingTargetVersion) || device.coinsingTargetVersion)
  );
  const rawStatus = isStaleTarget ? undefined : update?.status?.toLowerCase().trim();
  // When coinsingUpdateNeeded is true, any prior idle/clearing status ('idle', 'current', 'succeeded') is superseded by the pending assignment
  const status = (device.coinsingUpdateNeeded === true && (rawStatus === 'idle' || rawStatus === 'current' || rawStatus === 'succeeded'))
    ? undefined
    : rawStatus;
  const failureCode = isStaleTarget ? undefined : update?.failureCode?.toLowerCase().trim();

  // Check direction (is target older than installed?)
  let isRollback = false;
  if (targetVer && installedNorm) {
    try {
      isRollback = compareVersions(targetVer, installedNorm) < 0;
    } catch {
      isRollback = false;
    }
  }

  // 1. Check data_compat_deferred (amber pill: "Waiting: kiosk busy")
  if (failureCode === 'data_compat_deferred') {
    return {
      pillText: 'Waiting: kiosk busy',
      pillClass: 'status-partial',
      ariaLabel: 'Waiting: kiosk busy',
      isRollback,
      reason: update?.reason || 'data_compat_deferred'
    };
  }

  // 2. Kiosk reported update status
  if (status) {
    if (['downloading', 'verifying', 'staged', 'applying', 'verifying_health'].includes(status)) {
      const vText = targetVer ? ` v${targetVer}` : '';
      return {
        pillText: `Installing${vText}`,
        pillClass: 'status-sent',
        ariaLabel: `Installing${vText}`,
        isRollback
      };
    }

    if (status === 'succeeded') {
      return {
        pillText: 'Installed',
        pillClass: 'status-paid',
        ariaLabel: 'Installed',
        isRollback: false
      };
    }

    if (status === 'failed') {
      const vText = targetVer ? ` v${targetVer}` : '';
      return {
        pillText: `Failed${vText}`,
        pillClass: 'status-not-paid',
        ariaLabel: `Failed${vText}`,
        isRollback,
        reason: update?.errorMessage || update?.reason || failureCode || 'Update failed'
      };
    }

    if (status === 'rolled_back') {
      const rollbackVer = update?.previousVersion || installedNorm || '';
      const vText = rollbackVer ? ` to v${rollbackVer}` : '';
      return {
        pillText: `Rolled back${vText}`,
        pillClass: 'status-partial',
        ariaLabel: `Rolled back${vText}`,
        isRollback: true,
        reason: update?.errorMessage || update?.reason || 'Rolled back after failed install'
      };
    }

    if (status === 'gave_up') {
      const vText = targetVer ? ` v${targetVer}` : '';
      return {
        pillText: `Gave up${vText}`,
        pillClass: 'status-not-paid',
        ariaLabel: `Gave up${vText}`,
        isRollback,
        reason: update?.errorMessage || update?.reason || failureCode || 'Retry limit reached'
      };
    }

    if (status === 'idle' || status === 'current') {
      return {
        pillText: null,
        pillClass: '',
        ariaLabel: '',
        isRollback: false
      };
    }
  }

  // 3. Target set, kiosk has not yet reported status
  if (device.coinsingUpdateNeeded === true && targetVer) {
    if (isRollback) {
      return {
        pillText: `↩ Rollback to v${targetVer}`,
        pillClass: 'status-sent',
        ariaLabel: `Rollback to v${targetVer}`,
        isRollback: true
      };
    }
    return {
      pillText: `Pending v${targetVer}`,
      pillClass: 'status-sent',
      ariaLabel: `Pending v${targetVer}`,
      isRollback: false
    };
  }

  return {
    pillText: null,
    pillClass: '',
    ariaLabel: '',
    isRollback: false
  };
}

